import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";

import type { ContentItemResponseData, ContentItemType, FaqEntry } from "@chaochud/contracts";
import { ALLOWED_MEDIA_CONTENT_TYPES, MAX_MEDIA_FILE_SIZE_BYTES } from "@chaochud/validation";

import { adminOpsClient } from "@/lib/api-clients";

const ALLOWED_CONTENT_TYPES: readonly string[] = ALLOWED_MEDIA_CONTENT_TYPES;

const TYPE_LABEL: Record<ContentItemType, string> = {
  BANNER: "แบนเนอร์",
  ARTICLE: "บทความ",
  FAQ: "FAQ",
  PAGE: "หน้าเนื้อหา",
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: "ฉบับร่าง",
  PUBLISHED: "เผยแพร่",
};

const NEW_TYPE_OPTIONS: { value: ContentItemType; label: string }[] = [
  { value: "BANNER", label: "แบนเนอร์" },
  { value: "ARTICLE", label: "บทความ" },
  { value: "FAQ", label: "FAQ" },
  { value: "PAGE", label: "หน้าเนื้อหา" },
];

/**
 * `GET/POST /admin/content` (Round 2 UI handoff — AdminContentBuilder.dc.html).
 * Full CRUD against a real ContentItem backend — not a demo/UI-only screen.
 * `bannerImageUrl`/`coverImageUrl` upload straight to storage via
 * `POST /admin/content/:id/media/upload-url` (Pipeline — Remaining Work
 * §3 B2) — same presigned-URL pattern as seller product media / store
 * branding. Falls back to the dev/local stub URL when `STORAGE_S3_*` env
 * vars aren't set on the API (no real upload in that case, see
 * `StorageService`).
 */
export function ContentPage() {
  const [items, setItems] = useState<ContentItemResponseData[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [active, setActive] = useState<ContentItemResponseData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newType, setNewType] = useState<ContentItemType>("BANNER");
  const [newTitle, setNewTitle] = useState("");

  const load = async () => {
    try {
      const data = await adminOpsClient.listContent();
      setItems(data.items);
      const firstItem = data.items[0];
      if (!activeId && firstItem) {
        setActiveId(firstItem.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "โหลดไม่สำเร็จ");
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!activeId) {
      setActive(null);
      return;
    }
    adminOpsClient
      .getContent(activeId)
      .then(setActive)
      .catch((err) => setError(err instanceof Error ? err.message : "โหลดไม่สำเร็จ"));
  }, [activeId]);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 2400);
  };

  const patchActive = (patch: Partial<ContentItemResponseData>) => {
    setActive((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const handleSave = async () => {
    if (!active) return;
    setError(null);
    try {
      await adminOpsClient.updateContent(active.id, {
        title: active.title,
        bannerImageUrl: active.bannerImageUrl ?? undefined,
        linkUrl: active.linkUrl ?? undefined,
        startDate: active.startDate ?? undefined,
        endDate: active.endDate ?? undefined,
        coverImageUrl: active.coverImageUrl ?? undefined,
        body: active.body ?? undefined,
        faqItems: active.faqItems ?? undefined,
        excerpt: active.excerpt ?? undefined,
        metaTitle: active.metaTitle ?? undefined,
        metaDescription: active.metaDescription ?? undefined,
        slug: active.slug ?? undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "บันทึกไม่สำเร็จ");
      throw err;
    }
  };

  const handleSaveDraft = async () => {
    try {
      await handleSave();
      const updated = await adminOpsClient.saveDraftContent(active!.id);
      setActive(updated);
      await load();
      showToast("บันทึกฉบับร่างเรียบร้อยแล้ว");
    } catch {
      // error already set by handleSave
    }
  };

  const handlePublish = async () => {
    try {
      await handleSave();
      const updated = await adminOpsClient.publishContent(active!.id);
      setActive(updated);
      await load();
      showToast("เผยแพร่เนื้อหาเรียบร้อยแล้ว");
    } catch {
      // error already set by handleSave
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setError(null);
    try {
      const created = await adminOpsClient.createContent({ type: newType, title: newTitle.trim() });
      setNewTitle("");
      setShowNewForm(false);
      await load();
      setActiveId(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "สร้างไม่สำเร็จ");
    }
  };

  const updateFaqItem = (index: number, patch: Partial<FaqEntry>) => {
    setActive((prev) => {
      if (!prev) return prev;
      const faqItems = [...(prev.faqItems ?? [])];
      const current = faqItems[index] ?? { q: "", a: "" };
      faqItems[index] = { ...current, ...patch };
      return { ...prev, faqItems };
    });
  };

  const addFaqItem = () => {
    setActive((prev) => (prev ? { ...prev, faqItems: [...(prev.faqItems ?? []), { q: "", a: "" }] } : prev));
  };

  return (
    <div className="grid min-h-[calc(100vh-140px)] grid-cols-1 gap-0 md:grid-cols-[260px_1fr]">
      <aside className="border-r border-slate-line bg-slate-surface p-4">
        {showNewForm ? (
          <div className="mb-4 flex flex-col gap-2 rounded-lg border border-slate-line p-3">
            <select
              value={newType}
              onChange={(event) => setNewType(event.target.value as ContentItemType)}
              className="rounded-lg border border-slate-line px-2.5 py-1.5 text-[12.5px]"
            >
              {NEW_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              value={newTitle}
              onChange={(event) => setNewTitle(event.target.value)}
              placeholder="ชื่อหัวข้อ"
              className="rounded-lg border border-slate-line px-2.5 py-1.5 text-[12.5px]"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void handleCreate()}
                className="flex-1 rounded-lg bg-plum px-2.5 py-1.5 text-[12px] font-semibold text-white"
              >
                สร้าง
              </button>
              <button
                type="button"
                onClick={() => setShowNewForm(false)}
                className="rounded-lg border border-slate-line px-2.5 py-1.5 text-[12px]"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowNewForm(true)}
            className="mb-4 w-full rounded-lg bg-plum px-3 py-2.5 text-[13px] font-semibold text-white hover:opacity-90"
          >
            + เพิ่มเนื้อหาใหม่
          </button>
        )}

        <div className="flex flex-col gap-0.5">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveId(item.id)}
              className={`rounded-lg px-3 py-2.5 text-left ${
                activeId === item.id ? "border-l-[3px] border-plum bg-plum-subtle" : "border-l-[3px] border-transparent"
              }`}
            >
              <p className="text-[13px] font-semibold text-ink">{item.title}</p>
              <p className="mt-0.5 text-[11px] text-muted">
                {TYPE_LABEL[item.type]} · {STATUS_LABEL[item.status]}
              </p>
            </button>
          ))}
          {items.length === 0 && <p className="px-3 py-2 text-[12.5px] text-muted">ยังไม่มีเนื้อหา</p>}
        </div>
      </aside>

      <div className="max-w-3xl p-7">
        {error && <p className="mb-3 text-sm text-error-text">{error}</p>}

        {!active ? (
          <p className="text-sm text-muted">เลือกเนื้อหาทางซ้าย หรือสร้างใหม่</p>
        ) : (
          <>
            <div className="mb-5 flex items-center justify-between">
              <p className="font-heading text-lg font-bold text-ink">แก้ไขเนื้อหา — {TYPE_LABEL[active.type]}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void handleSaveDraft()}
                  className="rounded-lg border border-slate-line bg-white px-3.5 py-2 text-[12.5px] font-semibold text-ink"
                >
                  บันทึกฉบับร่าง
                </button>
                <button
                  type="button"
                  onClick={() => void handlePublish()}
                  className="rounded-lg bg-plum px-3.5 py-2 text-[12.5px] font-semibold text-white hover:opacity-90"
                >
                  เผยแพร่
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-4 rounded-xl border border-slate-line bg-white p-5.5">
              <div>
                <label className="mb-1.5 block text-[13px] font-semibold text-ink">ชื่อหัวข้อ</label>
                <input
                  value={active.title}
                  onChange={(event) => patchActive({ title: event.target.value })}
                  className="w-full rounded-lg border border-slate-line px-3.5 py-2.5 text-sm"
                />
              </div>

              {active.type === "BANNER" && (
                <>
                  <ContentImageUpload
                    contentId={active.id}
                    kind="BANNER"
                    label="ภาพแบนเนอร์"
                    recommendedSize="แนะนำ 1600×600px"
                    imageUrl={active.bannerImageUrl}
                    onUploaded={(publicUrl) => patchActive({ bannerImageUrl: publicUrl })}
                  />
                  <div>
                    <label className="mb-1.5 block text-[13px] font-semibold text-ink">ลิงก์ปลายทาง</label>
                    <input
                      value={active.linkUrl ?? ""}
                      onChange={(event) => patchActive({ linkUrl: event.target.value })}
                      placeholder="/search?occasion=wedding"
                      className="w-full rounded-lg border border-slate-line px-3.5 py-2.5 text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1.5 block text-[13px] font-semibold text-ink">เริ่มแสดงผล</label>
                      <input
                        type="date"
                        value={active.startDate?.slice(0, 10) ?? ""}
                        onChange={(event) => patchActive({ startDate: event.target.value })}
                        className="w-full rounded-lg border border-slate-line px-3.5 py-2.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[13px] font-semibold text-ink">สิ้นสุดการแสดงผล</label>
                      <input
                        type="date"
                        value={active.endDate?.slice(0, 10) ?? ""}
                        onChange={(event) => patchActive({ endDate: event.target.value })}
                        className="w-full rounded-lg border border-slate-line px-3.5 py-2.5 text-sm"
                      />
                    </div>
                  </div>
                </>
              )}

              {active.type === "ARTICLE" && (
                <>
                  <ContentImageUpload
                    contentId={active.id}
                    kind="COVER"
                    label="ภาพหน้าปก"
                    recommendedSize="แนะนำ 1200×630px"
                    imageUrl={active.coverImageUrl}
                    onUploaded={(publicUrl) => patchActive({ coverImageUrl: publicUrl })}
                  />
                  <div>
                    <label className="mb-1.5 block text-[13px] font-semibold text-ink">เนื้อหาบทความ</label>
                    <textarea
                      value={active.body ?? ""}
                      onChange={(event) => patchActive({ body: event.target.value })}
                      rows={8}
                      className="w-full rounded-lg border border-slate-line px-3.5 py-2.5 text-[13.5px]"
                    />
                  </div>
                </>
              )}

              {active.type === "FAQ" && (
                <div className="flex flex-col gap-2.5">
                  {(active.faqItems ?? []).map((entry, index) => (
                    <div key={index} className="rounded-lg border border-slate-line p-3.5">
                      <input
                        value={entry.q}
                        onChange={(event) => updateFaqItem(index, { q: event.target.value })}
                        placeholder="คำถาม"
                        className="mb-1.5 w-full border-none text-[13.5px] font-semibold text-ink outline-none"
                      />
                      <textarea
                        value={entry.a}
                        onChange={(event) => updateFaqItem(index, { a: event.target.value })}
                        placeholder="คำตอบ"
                        rows={2}
                        className="w-full border-none text-[13px] text-muted outline-none"
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addFaqItem}
                    className="self-start rounded-lg bg-slate-row px-3.5 py-2 text-[12.5px] font-semibold text-ink"
                  >
                    + เพิ่มคำถาม
                  </button>
                </div>
              )}

              {active.type === "PAGE" && (
                <div>
                  <label className="mb-1.5 block text-[13px] font-semibold text-ink">
                    เนื้อหาหน้า (เช่น เงื่อนไขการใช้งาน)
                  </label>
                  <textarea
                    value={active.body ?? ""}
                    onChange={(event) => patchActive({ body: event.target.value })}
                    rows={10}
                    className="w-full rounded-lg border border-slate-line px-3.5 py-2.5 text-[13.5px]"
                  />
                </div>
              )}

              {(active.type === "ARTICLE" || active.type === "PAGE") && (
                <div className="flex flex-col gap-4 border-t border-slate-line pt-4">
                  <p className="text-[12px] font-bold tracking-wide text-muted">SEO และลิงก์สาธารณะ</p>
                  <div>
                    <label className="mb-1.5 block text-[13px] font-semibold text-ink">Slug (ลิงก์)</label>
                    <input
                      value={active.slug ?? ""}
                      onChange={(event) => patchActive({ slug: event.target.value })}
                      placeholder="เช่น how-to-choose-a-wedding-dress"
                      className="w-full rounded-lg border border-slate-line px-3.5 py-2.5 text-sm"
                    />
                    {active.slug && active.type === "ARTICLE" && (
                      <p className="mt-1 text-[11px] text-muted">/articles/{active.slug}</p>
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[13px] font-semibold text-ink">สรุปย่อ (Excerpt)</label>
                    <textarea
                      value={active.excerpt ?? ""}
                      onChange={(event) => patchActive({ excerpt: event.target.value })}
                      rows={2}
                      maxLength={300}
                      placeholder="แสดงในหน้ารายการบทความ และเป็นค่าเริ่มต้นของ meta description"
                      className="w-full rounded-lg border border-slate-line px-3.5 py-2.5 text-[13.5px]"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[13px] font-semibold text-ink">Meta Title (ไม่บังคับ)</label>
                    <input
                      value={active.metaTitle ?? ""}
                      onChange={(event) => patchActive({ metaTitle: event.target.value })}
                      placeholder="ค่าเริ่มต้นใช้ชื่อหัวข้อด้านบน"
                      className="w-full rounded-lg border border-slate-line px-3.5 py-2.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[13px] font-semibold text-ink">
                      Meta Description (ไม่บังคับ)
                    </label>
                    <textarea
                      value={active.metaDescription ?? ""}
                      onChange={(event) => patchActive({ metaDescription: event.target.value })}
                      rows={2}
                      maxLength={300}
                      placeholder="ค่าเริ่มต้นใช้สรุปย่อด้านบน"
                      className="w-full rounded-lg border border-slate-line px-3.5 py-2.5 text-[13.5px]"
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {toast && (
          <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[oklch(0.2_0.006_260)] px-5.5 py-3 text-[13.5px] text-white">
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Uploads straight to storage via a presigned URL (`POST
 * /admin/content/:id/media/upload-url`, same pattern as product media /
 * store branding on `apps/web`). Only mints the URL and updates local form
 * state (`onUploaded`) — the actual field still saves with the rest of the
 * form via "บันทึกฉบับร่าง"/"เผยแพร่", same as every other field here.
 */
function ContentImageUpload({
  contentId,
  kind,
  label,
  recommendedSize,
  imageUrl,
  onUploaded,
}: {
  contentId: string;
  kind: "BANNER" | "COVER";
  label: string;
  recommendedSize: string;
  imageUrl: string | null;
  onUploaded: (publicUrl: string) => void;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(imageUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreviewUrl(imageUrl);
  }, [imageUrl]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!ALLOWED_CONTENT_TYPES.includes(file.type)) {
      setError("รองรับเฉพาะไฟล์ JPEG, PNG หรือ WEBP");
      return;
    }
    if (file.size > MAX_MEDIA_FILE_SIZE_BYTES) {
      setError("ไฟล์ใหญ่เกินไป (สูงสุด 5 MB)");
      return;
    }

    setError(null);
    setIsUploading(true);

    const optimisticUrl = URL.createObjectURL(file);
    setPreviewUrl(optimisticUrl);

    try {
      const upload = await adminOpsClient.requestContentMediaUploadUrl(contentId, {
        kind,
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      });

      const putResponse = await fetch(upload.uploadUrl, { method: "PUT", body: file });
      if (!putResponse.ok) {
        throw new Error(`อัปโหลดไม่สำเร็จ (status ${putResponse.status})`);
      }

      onUploaded(upload.publicUrl);
      setPreviewUrl(upload.publicUrl);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "อัปโหลดไม่สำเร็จ");
      setPreviewUrl(imageUrl);
    } finally {
      URL.revokeObjectURL(optimisticUrl);
      setIsUploading(false);
    }
  };

  return (
    <div>
      <label className="mb-1.5 block text-[13px] font-semibold text-ink">
        {label} <span className="font-normal text-muted">({recommendedSize})</span>
      </label>
      <div className="relative flex aspect-[16/6] items-center justify-center overflow-hidden rounded-lg border border-slate-line bg-slate-surface">
        {previewUrl ? (
          <img src={previewUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="text-[12px] text-muted">ยังไม่มีรูป</span>
        )}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-ink/40 text-[11px] font-medium text-white">
            กำลังอัปโหลด...
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="rounded-lg border border-slate-line bg-white px-3 py-1.5 text-[12px] font-semibold text-ink disabled:opacity-60"
        >
          เลือกรูปภาพ
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_CONTENT_TYPES.join(",")}
          className="hidden"
          onChange={(event) => void handleFileChange(event)}
        />
      </div>
      {error && <p className="mt-1 text-[11.5px] text-error-text">{error}</p>}
    </div>
  );
}
